import '../App.css';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import MainStructure from '../components/general/MainStructure';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../AuthContext';
import ScheduledPost from './components/ScheduledPost';
import PostedPost from './components/PostedPost';
import * as Icon from 'react-feather';
import {
  ResponsiveContainer, AreaChart, Area,
  XAxis, Tooltip as RTooltip,
} from 'recharts';

import myImage from '../assets/empty-box-2.png';
import { PlatformIcon, ProfilePicture } from '../components/general/AccountIcons';
import ProfileSetupBanner from '../components/general/ProfileSetupBanner';

const s = {
  sectionTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#334155',
    marginBottom: 12,
  },
  statCard: {
    background: '#fff',
    border: '1px solid #d1d9e0',
    borderRadius: 12,
    padding: '20px 24px',
  },
  statNum: {
    fontSize: '2rem',
    fontWeight: 600,
    color: '#0f172a',
    lineHeight: 1,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: '0.82rem',
    color: '#64748b',
    margin: 0,
  },
  statIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyBox: {
    background: '#f8fafc',
    border: '1px dashed #d1d9e0',
    borderRadius: 12,
    padding: '40px 24px',
    textAlign: 'center',
  },
  shortcutCard: {
    background: '#fff',
    border: '1px solid #d1d9e0',
    borderRadius: 12,
    padding: '20px',
    height: '100%',
  },
  shortcutIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f1f5f9',
    marginBottom: 14,
    flexShrink: 0,
  },
};

function StatCard({ icon, iconBg, iconColor, value, label }) {
  return (
    <div style={s.statCard} className="h-100">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div style={{ ...s.statIcon, background: iconBg }}>
          {React.cloneElement(icon, { size: 16, color: iconColor })}
        </div>
      </div>
      <div style={s.statNum}>{value}</div>
      <p style={s.statLabel}>{label}</p>
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const myContext = useAuth();
  const mySocialAccounts = myContext.accounts;
  const infoLoaded = myContext.infoLoaded;

  const [pendingTiktoks, setPendingTiktoks] = useState([]);
  const [analyticsSnap, setAnalyticsSnap] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!infoLoaded) return;
    fetch('https://api.schedpilot.com/wp-json/api/v1/dashboard-stats', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.code === 200) setStats(d); })
      .catch(() => {});
  }, [infoLoaded]);

  useEffect(() => {
    if (!infoLoaded) return;
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const params = new URLSearchParams({
      start_date:  start.toISOString().split('T')[0],
      end_date:    new Date().toISOString().split('T')[0],
      granularity: 'day',
    });
    fetch(`https://api.schedpilot.com/wp-json/api/v1/analytics-overview?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.code === 200) setAnalyticsSnap(d); })
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
  }, [infoLoaded]);

  useEffect(() => {
    if (!infoLoaded) return;
    const fetch_ = () => {
      fetch('https://api.schedpilot.com/wp-json/api/v1/getPendingTiktokPosts', {
        method: 'POST', credentials: 'include',
      })
        .then(r => r.json())
        .then(d => { if (d.posts) setPendingTiktoks(d.posts); })
        .catch(() => {});
    };
    fetch_();
    const t = setInterval(fetch_, 60000);
    return () => clearInterval(t);
  }, [infoLoaded]);

  if (!infoLoaded) {
    return <MainStructure activeItem="dashboard"><LoadingSpinner /></MainStructure>;
  }

  return (
    <MainStructure activeItem="dashboard">
      <div className="container-fluid px-0">

        {myContext.returnMembershipNotification()}

        <ProfileSetupBanner />

        {/* ── Header ───────────────────────────────────────────── */}
        <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            {myContext.picture && (
              <img
                src={myContext.picture}
                alt="avatar"
                className="rounded-circle"
                style={{ width: 44, height: 44, objectFit: 'cover', border: '2px solid #d1d9e0' }}
              />
            )}
            <div>
              <h1 style={{ fontSize: '1.3rem', fontWeight: 500, color: '#0f172a', margin: 0 }}>
                Good to see you, {myContext.name}
              </h1>
              <p style={{ fontSize: '0.85rem', color: '#7d8fa3', margin: 0 }}>
                Here's what's going on with your content
              </p>
            </div>
          </div>
          <button
            className="btn btn-sm sp-btn-ghost"
            style={{ border: '1px solid #d1d9e0', borderRadius: 8, color: '#475569', background: '#fff', fontSize: '0.85rem' }}
            onClick={() => navigate('/settings')}
          >
            <Icon.Settings size={14} className="me-1" /> Settings
          </button>
        </div>

        {/* ── Stats ─────────────────────────────────────────────── */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-4 col-lg">
            <StatCard
              icon={<Icon.Clock />}
              iconBg="#eff6ff" iconColor="#3b82f6"
              value={stats?.scheduled ?? '—'}
              label="Scheduled posts"
            />
          </div>
          <div className="col-6 col-md-4 col-lg">
            <StatCard
              icon={<Icon.CheckCircle />}
              iconBg="#f0fdf4" iconColor="#16a34a"
              value={stats?.published ?? '—'}
              label="Published posts"
            />
          </div>
          <div className="col-6 col-md-4 col-lg">
            <StatCard
              icon={<Icon.Users />}
              iconBg="#fdf4ff" iconColor="#9333ea"
              value={mySocialAccounts?.length ?? 0}
              label="Connected accounts"
            />
          </div>
          <div className="col-6 col-md-4 col-lg">
            <StatCard
              icon={<Icon.FileText />}
              iconBg="#fff7ed" iconColor="#ea580c"
              value={stats?.drafts ?? '—'}
              label="Drafts"
            />
          </div>
          <div className="col-6 col-md-4 col-lg">
            <StatCard
              icon={<Icon.AlertCircle />}
              iconBg="#fef2f2" iconColor="#dc2626"
              value={stats?.failed ?? '—'}
              label="Failed posts"
            />
          </div>
        </div>

        {/* ── Analytics snapshot ───────────────────────────────── */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <p style={s.sectionTitle} className="mb-0">Analytics snapshot · last 7 days</p>
          <button
            className="btn btn-sm sp-btn-ghost"
            style={{ fontSize: '0.78rem', color: '#64748b', border: '1px solid #d1d9e0', borderRadius: 8, background: '#fff' }}
            onClick={() => navigate('/analytics')}
          >
            Full analytics
          </button>
        </div>

        <div className="row g-3 mb-3">
          {[
            { key: 'impressions', label: 'Impressions', icon: <Icon.Eye />,           iconBg: '#eef2ff', iconColor: '#4f46e5' },
            { key: 'likes',       label: 'Likes',       icon: <Icon.Heart />,          iconBg: '#fef2f2', iconColor: '#ef4444' },
            { key: 'comments',    label: 'Comments',    icon: <Icon.MessageSquare />,  iconBg: '#f5f3ff', iconColor: '#8b5cf6' },
            { key: 'shares',      label: 'Shares',      icon: <Icon.Share2 />,         iconBg: '#f0fdf4', iconColor: '#10b981' },
          ].map(({ key, label, icon, iconBg, iconColor }) => {
            const val = analyticsSnap?.totals?.[key];
            const fmtVal = n => n == null ? '—' : n >= 1000000 ? (n/1000000).toFixed(1)+'M' : n >= 1000 ? (n/1000).toFixed(1)+'K' : n.toLocaleString();
            return (
              <div key={key} className="col-6 col-md-4 col-lg-3">
                <StatCard
                  icon={icon}
                  iconBg={iconBg}
                  iconColor={iconColor}
                  value={analyticsLoading ? '—' : fmtVal(val ?? 0)}
                  label={label}
                />
              </div>
            );
          })}
        </div>

        {/* Sparkline */}
        {!analyticsLoading && analyticsSnap?.trend?.length > 1 && (
          <div style={{ ...s.statCard, marginBottom: 24 }}>
            <div style={{ height: 90 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analyticsSnap.trend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="snapImpGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#4f46e5" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#4f46e5" stopOpacity={0}    />
                    </linearGradient>
                    <linearGradient id="snapLkGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}   />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" hide />
                  <RTooltip
                    contentStyle={{ fontSize: '0.72rem', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    formatter={(v, name) => [v?.toLocaleString() ?? 0, name.charAt(0).toUpperCase() + name.slice(1)]}
                    labelStyle={{ fontWeight: 600, color: '#334155', marginBottom: 2 }}
                  />
                  <Area type="monotone" dataKey="impressions" stroke="#4f46e5" strokeWidth={2} fill="url(#snapImpGrad)" dot={false} />
                  <Area type="monotone" dataKey="likes"       stroke="#ef4444" strokeWidth={1.5} fill="url(#snapLkGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {!analyticsLoading && (!analyticsSnap || analyticsSnap?.trend?.length === 0) && (
          <div style={{ ...s.emptyBox, marginBottom: 24 }}>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
              No analytics data yet — sync runs every 6 hours after publishing.
            </p>
          </div>
        )}

        {/* ── Connected accounts ────────────────────────────────── */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <p style={s.sectionTitle} className="mb-0">Connected accounts</p>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm sp-btn-ghost"
              style={{ fontSize: '0.78rem', color: '#64748b', border: '1px solid #d1d9e0', borderRadius: 8, background: '#fff' }}
              onClick={() => navigate('/social-accounts')}
            >
              <Icon.Plus size={13} className="me-1" /> Connect
            </button>
            <button
              className="btn btn-sm sp-btn-ghost"
              style={{ fontSize: '0.78rem', color: '#64748b', border: '1px solid #d1d9e0', borderRadius: 8, background: '#fff' }}
              onClick={() => navigate('/social-accounts')}
            >
              Manage
            </button>
          </div>
        </div>

        {mySocialAccounts.length === 0 ? (
          <div style={s.emptyBox} className="mb-4">
            <img src={myImage} alt="" style={{ width: 56, opacity: 0.35, marginBottom: 12 }} />
            <p style={{ color: '#7d8fa3', fontSize: '0.9rem', margin: 0 }}>
              No accounts connected yet.{' '}
              <span
                style={{ color: '#84cc16', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/social-accounts')}
              >
                Connect one now
              </span>
            </p>
          </div>
        ) : (
          <div className="row g-2 mb-4">
            {mySocialAccounts.map(account => (
              <div className="col-12 col-sm-6 col-lg-4 col-xl-3" key={account.id}>
                <div style={{ background: '#fff', border: '1px solid #b0bec5', borderRadius: 10, padding: '10px 14px' }}
                  className="d-flex align-items-center gap-2">
                  <PlatformIcon type={account.type} size={24} />
                  <ProfilePicture src={account.image} name={account.name} size={26} />
                  <span style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 500 }} className="text-truncate">
                    {account.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Pending TikToks ───────────────────────────────────── */}
        {pendingTiktoks.length > 0 && (
          <div className="mb-4">
            <p style={s.sectionTitle}>
              TikTok processing
              <span className="badge ms-2" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.65rem', borderRadius: 6 }}>
                {pendingTiktoks.length} in progress
              </span>
            </p>
            <div className="row g-2">
              {pendingTiktoks.map(post => (
                <div className="col-12 col-md-6 col-lg-4" key={post.publish_id}>
                  <div style={{ background: '#fff', border: '1px solid #d1d9e0', borderRadius: 10, padding: 14 }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <img src={post.account.image} style={{ width: 28, height: 28, borderRadius: '50%' }} alt="" />
                      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>@{post.account.name}</span>
                      <span className="ms-auto badge"
                        style={{ background: post.status === 'SEND_TO_USER_INBOX' ? '#dbeafe' : '#f0fdf4', color: post.status === 'SEND_TO_USER_INBOX' ? '#1d4ed8' : '#15803d', fontSize: '0.7rem', borderRadius: 6 }}>
                        {post.status === 'SEND_TO_USER_INBOX' ? 'Action needed' : 'Processing'}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0 }} className="text-truncate">
                      {post.title || post.content}
                    </p>
                    {post.status === 'SEND_TO_USER_INBOX' && (
                      <p style={{ fontSize: '0.78rem', color: '#3b82f6', marginTop: 8, marginBottom: 0 }}>
                        Check your TikTok app inbox to finalize this video.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Scheduled posts ───────────────────────────────────── */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <p style={s.sectionTitle} className="mb-0">Scheduled posts</p>
          {myContext.scheduled?.length > 0 && (
            <button
              className="btn btn-sm sp-btn-ghost"
              style={{ fontSize: '0.78rem', color: '#64748b', border: '1px solid #d1d9e0', borderRadius: 8, background: '#fff' }}
              onClick={() => navigate('/schedule-posts')}
            >
              View all
            </button>
          )}
        </div>

        {myContext.scheduled?.length === 0 ? (
          <div style={s.emptyBox} className="mb-4">
            <p style={{ color: '#7d8fa3', fontSize: '0.9rem', margin: 0 }}>
              No scheduled posts.{' '}
              <span
                style={{ color: '#84cc16', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/write-post')}
              >
                Write something
              </span>
            </p>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            {myContext.scheduled.slice(0, 3).map(item => (
              <ScheduledPost key={item.id} myItem={item} />
            ))}
          </div>
        )}

        {/* ── Latest posts ──────────────────────────────────────── */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <p style={s.sectionTitle} className="mb-0">Latest published</p>
          {myContext.published?.length > 0 && (
            <button
              className="btn btn-sm sp-btn-ghost"
              style={{ fontSize: '0.78rem', color: '#64748b', border: '1px solid #d1d9e0', borderRadius: 8, background: '#fff' }}
              onClick={() => navigate('/posted-items')}
            >
              View all
            </button>
          )}
        </div>

        {myContext.published?.length === 0 ? (
          <div style={s.emptyBox} className="mb-4">
            <p style={{ color: '#7d8fa3', fontSize: '0.9rem', margin: 0 }}>No published posts yet.</p>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            {myContext.published
              .slice(0, 3)
              .sort((a, b) => new Date(b.posted_date) - new Date(a.posted_date))
              .map(item => (
                <PostedPost key={item.id || item.pid} myItem={item} />
              ))}
          </div>
        )}

        {/* ── Shortcuts ─────────────────────────────────────────── */}
        <p style={s.sectionTitle}>Quick links</p>
        <div className="row g-3 mb-4">
          {[
            {
              icon: <Icon.BookOpen size={18} color="#3b82f6" />,
              bg: '#eff6ff',
              title: 'Help center',
              desc: 'Browse guides and tutorials to get the most out of SchedPilot.',
              label: 'Go to documentation',
              href: 'https://docs.schedpilot.com',
              external: true,
            },
            {
              icon: <Icon.HelpCircle size={18} color="#9333ea" />,
              bg: '#fdf4ff',
              title: 'FAQ',
              desc: 'Quick answers to the most common questions.',
              label: 'Go to FAQ',
              to: '/faq',
            },
          ].map(({ icon, bg, title, desc, label, href, to, external }) => (
            <div className="col-12 col-md-6" key={title}>
              <div style={s.shortcutCard}>
                <div style={{ ...s.shortcutIcon, background: bg }}>{icon}</div>
                <p style={{ fontSize: '0.9rem', fontWeight: 500, color: '#0f172a', marginBottom: 6 }}>{title}</p>
                <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 16 }}>{desc}</p>
                {external ? (
                  <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.82rem', color: '#475569', textDecoration: 'underline' }}>{label}</a>
                ) : (
                  <button
                    onClick={() => navigate(to)}
                    style={{ fontSize: '0.82rem', color: '#475569', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    {label}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>
    </MainStructure>
  );
}

export default Home;
